import { getStringDate } from "@/utils";
import axios from "axios";
import dayjs from "@/utils/dayjs";

const rawBaseUrl = import.meta.env.VITE_API_URL || "";
const normalizedBaseUrl = rawBaseUrl.replace(/\/api\/?$/, "").replace(/\/+$/, "");

const apiBase = axios.create({
    baseURL: normalizedBaseUrl,
    validateStatus: () => true,
});

export interface SimpleCategory {
    categoryId: number;
    categoryName: string;
}

export interface ProductByDate {
    sku: number;
    productName: string;
    categoryId: number;
    categoryName: string;
    receipt: number;
    price: number;
    categoryAndProductId: string;
    imagesUrl: string[];
}

const fetchProductsByAvailableDate = async (
    date: Date,
    timeRange: string
): Promise<ProductByDate[]> => {
    const urlParams = new URLSearchParams();
    urlParams.append("date", getStringDate(date));
    urlParams.append("timeRange", timeRange);

    const response = await apiBase.get(
        `/api/Calendar/availability?${urlParams.toString()}`
    );

    const responseWithoutAvailability = response.data as Array<
        Omit<ProductByDate, "availability">
    >;

    return responseWithoutAvailability.map((d) => ({
        ...d,
        categoryAndProductId: `${d.categoryId}_${d.sku}`,
    }));
};

interface CheckoutInputProduct {
    sku: number;
    categoryId: number;
    date: string;
    quantity: number;
    unitPrice: number;
    receiptId: number;
    timeRange: string;
    visitorName?: string;
    visitorDocument?: string;
    visitorBirthDate?: string;
}

interface CheckoutInput {
    tickets: Array<CheckoutInputProduct>;
    cupomCode?: string;
    orderHolder: {
        name: string;
        documentNumber: string;
        email: string;
        birthDate?: string;
        city?: string;
        state?: string;
        phoneNumber: string;
        countryId?: number;
    };
    totalAmount: number;
    callbackUrl?: string;
}

type ApiResponse<T> =
    | {
          message: string;
          errors: unknown[];
      }
    | T;

type ApiCheckoutResponse =
    | {
          locatorCode: string;
          url: string;
          id: string
      }
    | {
          message: string;
          details: string;
      };

const sendCheckout = async (data: CheckoutInput) => {
    const response = await apiBase.post("/api/Order", data);

    return response.data as ApiResponse<ApiCheckoutResponse>;
};

export interface ApiCalendarCategory {
    unitPrice: number;
    categoryName: string;
    categoryId: number;
}

export interface ApiCalendarSchedule {
    time: string;
    categories: ApiCalendarCategory[];
}

export interface ApiCalendarDay {
    date: string;
    seasonId: number;
    seasonName: string;
    seasonColor: string;
    availability: boolean;
    highDemand: boolean;
    schedules: ApiCalendarSchedule[];
}

const fetchCalendar = async (sku: number, startDate: Date, endDate: Date) => {
    const urlParams = new URLSearchParams();
    urlParams.append("startDate", getStringDate(startDate));
    urlParams.append("endDate", getStringDate(endDate));
    urlParams.append("sku", sku.toString());
    const response = await apiBase.get(`/api/Calendar?${urlParams.toString()}`);

    return response.data as ApiCalendarDay[];
};

export interface ApiAvailableDayItem {
    date: string;
    season: number;
}

const fetchAvailableDates = async (startDate: Date) => {
    const urlParams = new URLSearchParams();
    const date = dayjs(startDate).tz("America/Sao_Paulo");
    urlParams.append("month", (date.month() + 1).toString().padStart(2, "0"));
    urlParams.append("year", date.year().toString());
    const response = await apiBase.get(`/api/Calendar?${urlParams.toString()}`);

    return response.data as ApiAvailableDayItem[];
};

export interface ApiCouponResponse {
    isValid: boolean;
    message: string;
    percentage: 0;
}

const checkIfCouponIsValid = async (cupon: string) => {
    const urlParams = new URLSearchParams();
    urlParams.set("couponCode", cupon);
    const response = await apiBase.get(
        `/api/Coupons/validate?${urlParams.toString()}`,
        {}
    );

    return response.data as ApiCouponResponse;
};

export interface ApiVoucherInfo {
    qrCode: string;
    categoryName: string;
    visitDate: string;
    visitTime: string;
    visitorName?: string;
    visitorDocument?: string;
    buyerName?: string;
    buyerDocument?: string;
}

const fetchVoucherInfo = async (voucherId: string) => {
    const response = await apiBase.get(`/api/Ticket/${voucherId}`);

    return response.data as ApiVoucherInfo;
};

const fetchDateSchedules = async (date: Date) => {
    const urlParams = new URLSearchParams();
    urlParams.append("date", getStringDate(date));
    const response = await apiBase.get(
        `/api/Calendar/available-hours?${urlParams.toString()}`
    );

    return Array.isArray(response.data) ? response.data : [];
};

export interface ApiSalesStatus {
    date: string;
    online: boolean;
    message: string | null;
    closesAt: string | null;
    type?: number;
    badgeLabel?: string;
}

const fetchSalesStatus = async (date: Date): Promise<ApiSalesStatus> => {
    const urlParams = new URLSearchParams();
    urlParams.append("date", getStringDate(date));
    const response = await apiBase.get(
        `/api/Calendar/sales-status?${urlParams.toString()}`
    );

    if (response.status !== 200) {
        throw new Error(`sales-status respondeu ${response.status}`);
    }

    return response.data as ApiSalesStatus;
};

const sendContactForm = async (payload: {
    name: string;
    email: string;
    phoneNumber: string;
    subject: string;
    message: string;
    receiveMarketingEmails: boolean;
}): Promise<boolean> => {
    const result = await apiBase.post(
        "/api/Email/contact",
        JSON.stringify(payload),
        {
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    return result.status === 200;
};

const sendExcursaoForm = async (payload: {
    name: string;
    email: string;
    phoneNumber: string;
    peopleCount: string;
    plannedDate: string;
    schoolName: string;
    message: string;
    receiveMarketingEmails: boolean;
}): Promise<boolean> => {
    const result = await apiBase.post(
        "/api/Email/school",
        JSON.stringify(payload),
        {
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    return result.status === 200;
};

const sendEventForm = async (payload: {
    name: string;
    email: string;
    phoneNumber: string;
    peopleCount: string;
    plannedDate: string;
    selectedPackage: string;
    subject: string;
    message: string;
    receiveMarketingEmails: boolean;
}): Promise<boolean> => {
    const result = await apiBase.post(
        "/api/Email/party",
        JSON.stringify(payload),
        {
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    // Debug: log full server response so developers can see what's returned
    // in the browser console when the form is submitted.
    try {
        // Use console.debug to avoid polluting production logs unnecessarily
        // but still make the info available in devtools.
        // eslint-disable-next-line no-console
        console.debug("api.sendEventForm response", {
            status: result.status,
            data: result.data,
            headers: result.headers,
        });
    } catch (e) {
        /* ignore */
    }

    return result.status === 200;
};

const sendWorkForm = async (payload: {
    name: string;
    email: string;
    cellphone: string;
    areaOfInterest: string;
    message: string;
    receiveMarketingEmails: string;
    resume: File;
}): Promise<boolean> => {
    const formData = new FormData();
    formData.append("Name", payload.name);
    formData.append("Email", payload.email);
    formData.append("PhoneNumber", payload.cellphone);
    formData.append("AreaOfInterest", payload.areaOfInterest);
    formData.append("Message", payload.message);
    formData.append("ReceiveMarketingEmails", payload.receiveMarketingEmails);
    formData.append("Resume", payload.resume);

    const result = await apiBase.post("/api/Email/work-with-us", formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });

    return result.status === 200;
};

const sendCorporateForm = async (payload: {
    name: string;
    email: string;
    phoneNumber: string;
    peopleCount: string;
    plannedDate: string;
    message: string;
    receiveMarketingEmails: boolean;
}): Promise<boolean> => {
    const result = await apiBase.post(
        "/api/Email/business-event",
        JSON.stringify(payload),
        {
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    return result.status === 200;
};

const sendGroupForm = async (payload: {
    name: string;
    email: string;
    phoneNumber: string;
    peopleCount: string;
    plannedDate: string;
    message: string;
    receiveMarketingEmails: boolean;
}): Promise<boolean> => {
    const result = await apiBase.post(
        "/api/Email/group",
        JSON.stringify(payload),
        {
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    return result.status === 200;
};

interface CheckoutStatusResponse {
    locatorCode: string;
    status:
        | "Reserved"
        | "Pending"
        | "Failed"
        | "Paid"
        | "PaymentFailed"
        | "Cancelled";
    ticketIds?: string[] | null;
}

const fetchCheckoutStatus = async (
    locatorCode: string,
    orderId?: string
): Promise<CheckoutStatusResponse> => {
    const params = new URLSearchParams({});
    if (orderId) params.append("orderId", orderId);

    const result = await apiBase.get(
        `/status/${locatorCode}?${params.toString()}`
    );

    return result.data;
};

export interface CountryItem {
    idPais: number;
    nomePais: string;
    sigla3dig: string;
    sigla2dig: string;
    outrosNomes: string;
    nomeOficial: string;
    ddi: number | null;
    pathBandeira: string;
    codigoISO: string;
    temCidades: boolean;
}

export type CountryResponse = CountryItem[];

const fetchCountries = async (): Promise<CountryResponse> => {
    const result = await apiBase.get(`/api/Country`);

    return result.data;
};

const apiService = {
    sendCheckout,
    fetchCalendar,
    fetchAvailableDates,
    fetchProductsByAvailableDate,
    sendContactForm,
    sendWorkForm,
    sendCorporateForm,
    sendGroupForm,
    sendEventForm,
    sendExcursaoForm,
    checkIfCouponIsValid,
    fetchVoucherInfo,
    fetchDateSchedules,
    fetchSalesStatus,
    fetchCheckoutStatus,
    fetchCountries,
};

declare global {
    interface Window {
        apiService: typeof apiService;
    }
}

window.apiService = apiService;

export default apiService;
